import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { StoreModule } from "@/constants/store.ts";
import {
  AllowBlockListType,
  AllowListSubType,
  EmailSecurityAllowBlockItemType,
  PhishingAction,
} from "@/constants/phishing";
import { downloadFile } from "@/_helpers/download";
import type { Pagination } from "@/types";
import { useFiltersStore } from "@/_store/filters.module";
import type { EmailError } from "@/constants/emails";
import type { SecurityMode } from "@/constants/emails";
import type { QuarantineFolder, ScanSensitivityLevels } from "@/constants/emails";
import { PhishingReportFrequency, PhishingReportSelectionType } from "@/constants/emails";
import axios from "axios";
import type { SortOrders } from "@/components/ListTableHeader.vue";
import type { UserLabel } from "@/_store/users-settings/user-labels.module.ts";
import type { UserGroupSettingsItem } from "@/_store/users-settings/user-groups-tab.module.ts";

export interface SortItem {
  key: string;
  order?: boolean | SortOrders.ASC | SortOrders.DESC;
}

export interface BlockedEmailReportUser {
  email: string;
  userId: string;
}

export interface EmailSecurityReportConfig {
  selectionType?: PhishingReportSelectionType;
  labelsEnabled?: boolean;
  groupsEnabled?: boolean;
  usersEnabled?: boolean;
  labels?: UserLabel[];
  groups?: UserGroupSettingsItem[];
  users?: BlockedEmailReportUser[];
}

export interface BlockedEmailReportsConfig extends EmailSecurityReportConfig {
  enabled: boolean;
  frequency: PhishingReportFrequency;
}

export interface AutoForwardingBlockingPolicyConfig extends EmailSecurityReportConfig {
  enabled: boolean;
}

export const defaultBlockedEmailReportsConfig: BlockedEmailReportsConfig = {
  enabled: false,
  selectionType: PhishingReportSelectionType.ALL,
  frequency: PhishingReportFrequency.DAILY,
  labelsEnabled: false,
  groupsEnabled: false,
  usersEnabled: false,
  labels: [],
  groups: [],
  users: [],
};

export const defaultAutoForwardingBlockingPolicyConfig: AutoForwardingBlockingPolicyConfig = {
  enabled: false,
  selectionType: PhishingReportSelectionType.ALL,
  labelsEnabled: false,
  groupsEnabled: false,
  usersEnabled: false,
  labels: [],
  groups: [],
  users: [],
};

export interface EmailSecuritySettings {
  securityEnabled: boolean;
  scanMalware: {
    enabled: boolean;
    action: SecurityMode;
  };
  scanSpam: {
    enabled: boolean;
    action: SecurityMode;
  };
  scanPhishing: {
    enabled: boolean;
    action: SecurityMode;
  };
  scanPromptInjection: {
    enabled: boolean;
    action: SecurityMode;
  };
  scanNewlyRegisteredDomains: {
    enabled: boolean;
    action: SecurityMode;
  };
  scanEncryptedAttachments: {
    enabled: boolean;
    action: SecurityMode;
  };
  scanUnverifiedSender: {
    enabled: boolean;
    action: SecurityMode;
  };
  scanUnknownExternalSender: {
    enabled: boolean;
    action: SecurityMode;
  };
  quarantineFileTypesEnabled: boolean;
  quarantineFileTypes: string[];
  quarantineCustomFileTypes: string[];
  quarantineFolder: QuarantineFolder;
  expirationEnabled: boolean;
  expirationCountDays: number;
  restrictForwarding: boolean;
  securityAwarenessConfigDto: SecurityAwarenessConfigDto;
  exclusionSecuritySettingsDto: {
    emailSubjectKeywordsEnabled: boolean;
    emailSubjectKeywords: string[];
  };
  scanSensitivity: ScanSensitivityLevels;
  adminEmail: string;
  blockedEmailReportsConfig: BlockedEmailReportsConfig;
  autoForwardingBlockingPolicy: AutoForwardingBlockingPolicyConfig;
  linkGuardSettings: {
    enabled: boolean;
  };
}

export interface ProviderConfiguration {
  name: string;
  terms: string[];
  headers: { name: string; value: string }[];
}

export interface ProviderConfiguration {
  name: string;
  terms: string[];
  headers: { name: string; value: string }[];
}

export interface SecurityAwarenessConfigDto {
  enabled: boolean;
  providerConfigurations: ProviderConfiguration[];
}

export interface AllowBlockListItem {
  name: string;
  type: EmailSecurityAllowBlockItemType;
  allowList: boolean;
  inheritedFromParent: boolean;
  senderClassificationType: AllowListSubType;
  updateTimestamp: number;
  description?: string;
  classifiedBy?: string;
}

export interface EmailSettingsState {
  settings: EmailSecuritySettings;
  showSkeletonLoader: boolean;
  allowBlockList: AllowBlockListItem[];
  pages: number;
  total: number;
  pagination: Pagination;
  loading: boolean;
  sortingOptions?: string;
}

export interface RemoveFromAllowBlockListPayload {
  name: string;
  listType: AllowBlockListType;
  senderClassificationType: AllowListSubType;
}

export interface AllowBlockListForm {
  items: string[];
  errors: EmailError[];
  action: PhishingAction;
  description?: string;
}

export interface ValidateAllowBlockTermsPayload {
  items: string[];
  allowList: boolean;
}

export interface ValidateAllowBlockTermsResponse {
  invalidTerms: string[];
  // The subset of invalidTerms, with the specific reason of it already existing in the list
  duplicateTerms: string[];
  errorMessage: string;
}

export interface TldTermsResponse {
  terms: string[];
}

export const defaultEmailSecuritySettingsState = {
  settings: {} as EmailSecuritySettings,
  showSkeletonLoader: false,
  allowBlockList: [],
  pages: 0,
  total: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  sortingOptions: undefined,
  loading: false,
};

export const useEmailSettingsStore = defineStore(StoreModule.EMAIL_SETTINGS_STORE, {
  state: (): EmailSettingsState => ({ ...defaultEmailSecuritySettingsState }),
  actions: {
    setPagination(pagination: Pagination) {
      this.pagination = pagination;
    },
    async setSortingOptions(sortingOptions: SortItem[]) {
      this.sortingOptions = sortingOptions[0]
        ? `${sortingOptions[0].key},${sortingOptions[0].order}`
        : undefined;
    },
    resetPagination() {
      this.pagination = { ...defaultEmailSecuritySettingsState.pagination };
    },
    async getEmailsSettings() {
      this.showSkeletonLoader = true;

      const request = {
        ...api.emailsSettings(),
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.settings = data;
      } catch (error) {
        console.error(error);
      }

      this.showSkeletonLoader = false;
    },
    async updateEmailsSettings(newSettings: EmailSecuritySettings): Promise<void> {
      const request = {
        ...api.emailsSettings(),
        method: "put",
        data: newSettings,
      };

      try {
        await axiosInstance.request(request);
        this.settings = newSettings;
      } catch (error) {
        console.error(error);
      }
    },
    async resetEmailFileTypes() {
      const request = {
        ...api.resetEmailFileTypes(),
        method: "post",
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.settings = data;
      } catch (error) {
        console.error(error);
      }
    },
    async searchUsers(email: string) {
      try {
        const request = {
          ...api.searchEmailUsers(email),
        };
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    async getAllowBlockList() {
      this.showSkeletonLoader = true;
      this.loading = true;

      const filtersStore = useFiltersStore();
      const filters = filtersStore.filters.allowBlockListFilters;
      const request = {
        ...api.getPhishingAllowBlackList(),
        params: {
          ...filters,
          ...this.pagination,
          sort: this.sortingOptions,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);

        this.allowBlockList = data.items;
        this.total = data.total;
      } catch (error) {
        console.error(error);
      }

      this.showSkeletonLoader = false;
      this.loading = false;
    },
    async removeFromAllowlist(payload: AllowBlockListItem) {
      await this.removeFromAllowBlockList({
        name: payload.name,
        listType: AllowBlockListType.ALLOW,
        senderClassificationType: payload.senderClassificationType,
      });
    },
    async removeFromBlocklist(payload: AllowBlockListItem) {
      await this.removeFromAllowBlockList({
        name: payload.name,
        listType: AllowBlockListType.BLOCK,
        senderClassificationType: payload.senderClassificationType,
      });
    },
    async addToAllowBlockList(payload: AllowBlockListForm) {
      const { items, action, description } = payload;
      const data = {
        items,
        allowList: action === PhishingAction.ADD_TO_ALLOWLIST,
        description,
      };

      const request = {
        ...api.getPhishingAllowBlackList(),
        data,
        method: "POST",
      };

      try {
        await axiosInstance.request(request);

        await this.getAllowBlockList();
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    async validateAllowBlockTerms(
      payload: ValidateAllowBlockTermsPayload
    ): Promise<ValidateAllowBlockTermsResponse> {
      const request = {
        ...api.validatePhishingAllowBlockList(),
        data: payload,
      };

      const { data } = await axiosInstance.request<ValidateAllowBlockTermsResponse>(request);

      return data;
    },
    async getExistingTlds(): Promise<string[] | null> {
      try {
        const { data } = await axiosInstance.request<TldTermsResponse>({
          ...api.getPhishingExistingTlds(),
        });

        return data?.terms ?? [];
      } catch (error) {
        console.error("Failed to fetch existing TLDs:", error);

        return null;
      }
    },
    async getRestrictedTlds(): Promise<string[] | null> {
      try {
        const { data } = await axiosInstance.request<TldTermsResponse>({
          ...api.getPhishingRestrictedTlds(),
        });

        return data?.terms ?? [];
      } catch (error) {
        console.error("Failed to fetch restricted TLDs:", error);

        return null;
      }
    },
    async removeFromAllowBlockList(payload: RemoveFromAllowBlockListPayload) {
      const request = {
        ...api.getPhishingAllowBlackList(),
        params: payload,
        method: "DELETE",
      };

      try {
        await axiosInstance.request(request);

        await this.getAllowBlockList();
      } catch (error) {
        console.error(error);
      }
    },
    async importAllowBlockListFromCsv(payload: File) {
      const data = new FormData();
      data.append("file", payload);
      const request = {
        ...api.importAllowBlockList(),
        data,
      };

      return axiosInstance.request(request);
    },
    async downloadAllowBlockListExampleCsv() {
      const request = {
        ...api.getPhishingAllowBlackListExampleCsv(),
      };

      try {
        const { data, headers } = await axiosInstance.request(request);
        const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
        const fileName = "AllowBlockListExample.csv";

        downloadFile(blob, fileName);
      } catch (error) {
        console.error(error);
      }
    },
    async reportPhishingImpersonation({
      token,
      ...data
    }: {
      allowList?: boolean;
      syncToken: string;
      emailAddress: string;
      token: string;
    }): Promise<boolean> {
      const request = {
        ...api.reportPhishingImpersonation(),
        data,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      try {
        // using axios here because we need to avoid interceptor for this particular request as it uses it's own token
        await axios.request(request);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
  },
});
